import { computed, type ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { Actions, Subjects } from '@boqnet/permissions';
import { useCan } from '@/composables/use-can';
import { useAuthStore } from '@/stores/auth.store';
import { usePermissionStore } from '@/stores/permission.store';
import { selectedOrgId } from '@/common/utils/role.utils';

/**
 * The three visibility tiers of the Bulk Orders surface (DL-06, FR-16).
 *
 * - `capable` — may initiate a bulk order. Tab visible, create enabled.
 * - `viewer` — may see bulk orders but not create one. Tab visible, create **disabled with its
 *   reason exposed** rather than hidden, because most users will not hold the new
 *   `PROJ_PROC_OFF` capability and a silently missing control reads as broken.
 * - `none` — no bulk visibility at all. The tab itself is absent; a site-scope user meets bulk
 *   only through a child order's parent reference on the Orders tab.
 */
export type BulkOrderTier = 'capable' | 'viewer' | 'none';

export interface BulkOrderCapability {
  tier: ComputedRef<BulkOrderTier>;
  /** The tab is rendered and its route reachable. True for `capable` and `viewer`. */
  canListBulkOrders: ComputedRef<boolean>;
  /** The create affordance is enabled. True for `capable` only. */
  canCreateBulkOrder: ComputedRef<boolean>;
  /**
   * Whether a bulk order may be **initiated on this specific project**.
   *
   * An **affordance**, not a guard: creation is re-authorised server-side at
   * `POST /bulk-orders` (batch A.4). Use it to disable a choice with a reason, never as the only
   * thing standing between a user and the act.
   */
  canInitiateOnProject: (projectId: string) => boolean;
  /**
   * Whether the answers above are final. Before LaunchDarkly and the boot-time role refresh have
   * both settled they can still change, so nothing may be refused on them yet (PE-1081).
   */
  isResolved: ComputedRef<boolean>;
}

/**
 * Resolves the viewer's bulk-order tier through `useCan()`.
 *
 * Context: the create question is asked **per project**, never unscoped, because a tab-level
 * check has no single project. CASL's bare-subject check cannot tell "may create here" from "may
 * create somewhere" and answers `true` for a conditionally-granted role — and the org tier holds
 * `List` and `View` and never `Create` (DL-50), so "capable" means capable *on some project*.
 */
export const useBulkOrderCapability = (): BulkOrderCapability => {
  const { can } = useCan();
  const { loggedInUser } = storeToRefs(useAuthStore());
  const permissionStore = usePermissionStore();

  const projectIds = computed<string[]>(() => {
    const org = loggedInUser.value?.roles?.organisations?.find(
      (candidate) => candidate.id === selectedOrgId.value,
    );

    return (org?.projects ?? []).map((project) => project.id);
  });

  const canCreateBulkOrder = computed(() =>
    projectIds.value.some((projectId) => can(Actions.CREATE, Subjects.BULK_ORDER, { projectId })),
  );

  /**
   * List is asked unscoped first — the org tier's grant is unconditional and is how back office
   * reaches the capability (DL-51) — then per project, which is where the project-scoped roles
   * hold it. Both engines answer the unscoped list question the same way, unlike create: the
   * adapter answers "role held anywhere" for a scopeless read precisely because that is the
   * navigation question a tab asks before a project is chosen.
   */
  const canListBulkOrders = computed(
    () =>
      can(Actions.LIST, Subjects.BULK_ORDER) ||
      projectIds.value.some((projectId) => can(Actions.LIST, Subjects.BULK_ORDER, { projectId })),
  );

  const tier = computed<BulkOrderTier>(() => {
    if (!canListBulkOrders.value) return 'none';
    return canCreateBulkOrder.value ? 'capable' : 'viewer';
  });

  /**
   * The same per-project question `canCreateBulkOrder` asks, for one named project.
   *
   * Asked through `useCan()` rather than by inspecting roles here, so the project picker and the
   * Orders tab's create affordance are answering with one engine and cannot disagree. A project the
   * token carries no role for — which an org-wide role can still *load* the bill for, since the
   * item endpoints gate on `ViewProjectFinance` — is simply absent from the ability's conditions
   * and answers false, which is the right answer.
   *
   * ⚠️ **Known parity gap, and it is not this composable's to close.** The API's
   * `BulkOrderPermissionService.CanCreate` is
   * `OrgUserRoles.Contains(ORG_MEMB) && AssignedToProject(user, project, [PROJ_PROC_OFF])`, and the
   * frontend's engine models only the second conjunct — `ability.factory.ts` grants
   * `onProject(Subjects.BULK_ORDER, [CREATE, READ, LIST])` with no organisation condition. So this
   * is marginally *wider* than the server in one case: an organisation entry carrying no
   * `ORG_MEMB` (VendorControl roles share that array, per `permissions.md`). Closing it belongs in
   * `@boqnet/permissions` with its three-to-five definitions updated together, not in a bespoke
   * role check at one call site, which would drift from every other consumer. Wider is the safe
   * direction for an affordance whose act is re-authorised server-side.
   */
  const canInitiateOnProject = (projectId: string): boolean =>
    !!projectId && can(Actions.CREATE, Subjects.BULK_ORDER, { projectId });

  const isResolved = computed(
    () =>
      permissionStore.isInitialized &&
      permissionStore.areRoleFlagsResolved &&
      permissionStore.areRolesRefreshed,
  );

  return { tier, canListBulkOrders, canCreateBulkOrder, canInitiateOnProject, isResolved };
};
